-- | Indexed work-queue document lookup helpers.
-- |
-- | The work queue still carries this structure directly during the migration,
-- | but callers should import it through this module so the durable queue state
-- | can be split cleanly later.
module Steps.WorkQueue.DocumentIndex where

import Common

import API.FormBase (FormID)
import API.Document (Document)
import API.Types (DocumentID, StudentID)
import Data.Array as Arr
import Data.List (List)
import Data.Map (Map)
import Data.Map as Map
import Data.Unfoldable as Unfoldable

type DocumentLookup =
    { studentId :: StudentID
    , formId :: FormID
    , documentId :: Maybe DocumentID
    }

type DocumentMap =
    { docs :: Map DocumentID Document
    , byLookup :: Map DocumentLookup DocumentID
    }

emptyDocMap :: DocumentMap
emptyDocMap = { docs: Map.empty, byLookup: Map.empty }

insertDoc :: Document -> DocumentMap -> DocumentMap
insertDoc doc dm = dm
    { docs = Map.insert doc.id doc dm.docs
    , byLookup = Map.insert (documentLookup doc) doc.id dm.byLookup
    }

listDocs :: DocumentMap -> List Document
listDocs dm = Map.values dm.docs

getDocById :: DocumentID -> DocumentMap -> Maybe Document
getDocById id dm = Map.lookup id dm.docs

getDocByLookup :: DocumentLookup -> DocumentMap -> Maybe Document
getDocByLookup dl dm = do
    id <- Map.lookup dl dm.byLookup
    getDocById id dm

documentLookup :: Document -> DocumentLookup
documentLookup doc =
    { studentId: doc.student.id
    , formId: doc.formId
    , documentId: Just doc.id
    }

matchStudent :: StudentID -> DocumentLookup -> Boolean
matchStudent student docRef =
    student == docRef.studentId

matchForm :: FormID -> DocumentLookup -> Boolean
matchForm form docRef =
    form == docRef.formId

filterOnStudent :: StudentID -> DocumentMap -> Array Document
filterOnStudent student dm =
    Arr.concatMap Unfoldable.fromMaybe
    $ Arr.fromFoldable
    $ map doLookup
    $ Map.values
    $ Map.filterKeys (matchStudent student) dm.byLookup
  where
    doLookup id = Map.lookup id dm.docs

filterOnStudentAndForm
    :: StudentID
    -> FormID
    -> DocumentMap
    -> DocumentMap
filterOnStudentAndForm student form dm = dm
    { byLookup = Map.filterKeys (\docRef ->
                    matchStudent student docRef && matchForm form docRef)
                    dm.byLookup
    }
